import { useCallback, useRef, useState } from 'react';
import { Alert, Button, Form, Result, Typography } from 'antd';
import { useHistory } from 'react-router-dom';

import RegisterAccountForm, {
  RegisterAccountLoginValidationStatus,
} from '@/components/Register/RegisterAccountForm';
import {
  ACTIVATION_LINK_INVALID_MESSAGE,
  ACTIVATION_SUCCESS_MESSAGE,
  ACTIVATION_UNEXPECTED_ERROR_MESSAGE,
} from '@/core/domain/enterprise/Companies/Activation/Activation.constants';
import {
  useCompanyActivate,
  useCompanyActivationLoginVerify,
} from '@/core/domain/enterprise/Companies/Activation/Activation.hooks';
import { CompanyActivationFormValues } from '@/core/domain/enterprise/Companies/Activation/Activation.types';
import { getActivationErrorMessage } from '@/core/domain/enterprise/Companies/Activation/Activation.utils';
import { useActivationHashPage } from '../../ActivationHashPage.context';

export default function ActivationForm() {
  const history = useHistory();
  const { activationToken, isTokenValid, validationFinished } =
    useActivationHashPage();
  const [form] = Form.useForm<CompanyActivationFormValues>();
  const [loginValidationStatus, setLoginValidationStatus] =
    useState<RegisterAccountLoginValidationStatus>('idle');
  const [submitError, setSubmitError] = useState<string>();
  const isSubmittingRef = useRef(false);

  const { mutateAsync: verifyLogin } = useCompanyActivationLoginVerify();
  const companyActivate = useCompanyActivate();

  function getSubmitHint(
    validationFinished: boolean,
    isTokenValid: boolean,
    loginValidationStatus: RegisterAccountLoginValidationStatus,
  ) {
    if (!validationFinished) return 'Aguarde a validação do link.';
    if (!isTokenValid) return 'Este link não está disponível para ativação.';
    if (loginValidationStatus === 'idle') {
      return 'Informe um login para verificarmos a disponibilidade.';
    }
    if (loginValidationStatus === 'validating') {
      return 'Validando disponibilidade do login.';
    }
    if (loginValidationStatus === 'invalid') {
      return 'Corrija o login para continuar.';
    }

    return undefined;
  }

  const canSubmit =
    validationFinished &&
    isTokenValid &&
    loginValidationStatus === 'valid' &&
    !companyActivate.isLoading;
  const submitHint = getSubmitHint(
    validationFinished,
    isTokenValid,
    loginValidationStatus,
  );

  const validateLogin = useCallback(
    async (login: string) => {
      if (!activationToken || !isTokenValid) {
        return {
          isValid: false,
          reason: ACTIVATION_LINK_INVALID_MESSAGE,
        };
      }

      return verifyLogin({
        user: { login },
      });
    },
    [activationToken, isTokenValid, verifyLogin],
  );

  function handleLoginRedirect() {
    history.push('/login');
  }

  async function handleFinish(values: CompanyActivationFormValues) {
    if (
      !activationToken ||
      !isTokenValid ||
      loginValidationStatus !== 'valid' ||
      companyActivate.isLoading ||
      isSubmittingRef.current
    ) {
      return;
    }

    setSubmitError(undefined);
    isSubmittingRef.current = true;

    try {
      await companyActivate.mutateAsync({
        activationToken,
        data: {
          user: values,
        },
      });
    } catch (error) {
      const errorMessage = getActivationErrorMessage(error);
      const errorText = errorMessage.toLowerCase();

      if (
        errorText.includes('login') &&
        (errorText.includes('existe') || errorText.includes('exist'))
      ) {
        form.setFields([
          {
            name: 'login',
            errors: [errorMessage || 'Já existe um usuário com este login.'],
          },
        ]);
        setLoginValidationStatus('invalid');
        return;
      }

      setSubmitError(ACTIVATION_UNEXPECTED_ERROR_MESSAGE);
    } finally {
      isSubmittingRef.current = false;
    }
  }

  if (companyActivate.isSuccess) {
    return (
      <Result
        className="activation-hash-page__success"
        status="success"
        title="Cadastro realizado"
        subTitle={ACTIVATION_SUCCESS_MESSAGE}
        extra={
          <Button type="primary" size="large" onClick={handleLoginRedirect}>
            fazer login
          </Button>
        }
      />
    );
  }

  return (
    <>
      {!!submitError && (
        <Alert
          type="error"
          showIcon
          message={submitError}
          style={{ marginBottom: 24 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <div className="activation-hash-page__form-guidance">
          <Typography.Text strong>Defina os dados de acesso</Typography.Text>
          <Typography.Paragraph type="secondary">
            Informe um login disponível e uma senha segura.
          </Typography.Paragraph>
        </div>

        <RegisterAccountForm
          validateLogin={validateLogin}
          onLoginValidationChange={setLoginValidationStatus}
        />

        <div className="activation-hash-page__actions">
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={companyActivate.isLoading}
            disabled={!canSubmit}
          >
            {companyActivate.isLoading
              ? 'Concluindo cadastro'
              : 'Concluir cadastro'}
          </Button>

          {!!submitHint && (
            <Typography.Text
              className="activation-hash-page__submit-hint"
              type="secondary"
            >
              {submitHint}
            </Typography.Text>
          )}
        </div>
      </Form>
    </>
  );
}
